import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminService } from '../../../service/admin.service';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { PrimengImports } from '../../../shared/primeng-imports';
import { ConfirmationService, MessageService } from 'primeng/api';

interface ProcessTransactionRequest {
  action: 'approve' | 'reject';
  referenceNumber?: string;
  notes?: string;
}

@Component({
  selector: 'app-transactions-approval-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideIconsModule, PrimengImports],
  templateUrl: './transactions-approval-tab.component.html',
  styleUrl: './transactions-approval-tab.component.css',
  providers:[ConfirmationService, MessageService]

})
export class TransactionsApprovalTabComponent implements OnInit, OnDestroy {
  transactions: any[] = [];
  filteredTransactions: any[] = [];
  
  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  
  // Loading states
  isLoading: boolean = false;
  isUpdating: boolean = false;
  
  // Filters
  statusFilter: string = 'all';
  searchTerm: string = '';
  dateRange: Date[] | undefined;
  
  // Modal state
  showConfirmModal: boolean = false;
  selectedTransaction: any | null = null;
  pendingAction: 'approve' | 'reject' | null = null;
  
  // Form fields for processing transaction
  referenceNumber: string = '';
  notes: string = '';

  // Responsive table controls
  responsiveLayout: 'stack' | 'scroll' = 'scroll';
  tableMinWidth = '60rem';
statusOptions: { label: string; value: string }[] = [];
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

     
  

  ngOnInit(): void {
    this.setResponsive();
    this.loadTransactions();
     this.statusOptions = [
      { label: 'All Status', value: 'all' },
      { label: 'Pending', value: 'Pending' },
      { label: 'Approved', value: 'Approved' },
      { label: 'Rejected', value: 'Rejected' }
    ];

    // Debounced search subscription
    this.searchSubject.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe((search) => {
      this.searchTerm = search;
      this.currentPage = 1;
      this.loadTransactions();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  confirmDelete(transaction: any): void {
    this.confirmationService.confirm({
      header: 'Delete Transaction',
      message: 'Are you sure you want to delete this transaction? This action cannot be undone.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptButtonStyleClass: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg',
      rejectButtonStyleClass: 'bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg',
      accept: () => {
        this.isUpdating = true;
        this.adminService.deleteTransaction(transaction._id).subscribe({
          next: (response: any) => {
            if (response?.success) {
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Transaction deleted successfully' });
              this.loadTransactions();
            } else {
              const msg = response?.message || 'Failed to delete transaction';
              this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
            }
            this.isUpdating = false;
          },
          error: (error) => {
            this.isUpdating = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error deleting transaction' });
            console.error('Error deleting transaction:', error);
          }
        });
      }
    });
  }

  private setResponsive(width = window.innerWidth) {
    if (width < 640) { // < sm
      this.responsiveLayout = 'stack';
      this.tableMinWidth = '0';
    } else if (width < 1024) { // sm-md
      this.responsiveLayout = 'scroll';
      this.tableMinWidth = '48rem';
    } else {
      this.responsiveLayout = 'scroll';
      this.tableMinWidth = '60rem';
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.setResponsive();
  }

  loadTransactions(): void {
    this.isLoading = true;
    const startDate = this.dateRange && this.dateRange[0] ? this.dateRange[0].toISOString() : undefined;
    const endDate = this.dateRange && this.dateRange[1] ? this.dateRange[1].toISOString() : undefined;

    this.adminService.getAllTransactions(this.currentPage, this.itemsPerPage, this.searchTerm, this.statusFilter, startDate, endDate).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.transactions = response.data.transactions;
          this.filteredTransactions = this.transactions;
          this.totalItems = response.data.pagination.total;
          this.totalPages = response.data.pagination.totalPages;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading transactions:', error);
        this.isLoading = false;
      }
    });
  }

  // Called for non-search filters (status/date) to apply immediately
  onFilterChange(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  // Called from input (debounced)
  onSearchInput(event: any): void {
    const value = event?.target?.value ?? '';
    // push to subject; actual search will run after debounce
    this.searchSubject.next(value.trim());
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.onFilterChange();
  }

  showConfirmation(transaction: any, action: 'approve' | 'reject'): void {
    this.selectedTransaction = transaction;
    this.pendingAction = action;
    this.showConfirmModal = true;
    
    // Clear previous form data
    this.referenceNumber = '';
    this.notes = '';
  }

  confirmAction(): void {
    if (this.selectedTransaction && this.pendingAction) {
      this.processTransaction(this.selectedTransaction, this.pendingAction);
    }
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
    this.selectedTransaction = null;
    this.pendingAction = null;
    this.referenceNumber = '';
    this.notes = '';
  }

  processTransaction(transaction: any, action: 'approve' | 'reject'): void {
    this.isUpdating = true;
    
    const requestData: ProcessTransactionRequest = {
      action: action
    };
    
    if (this.referenceNumber.trim()) {
      requestData.referenceNumber = this.referenceNumber.trim();
    }
    
    if (this.notes.trim()) {
      requestData.notes = this.notes.trim();
    }

    this.adminService.processTransaction(transaction._id, requestData).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Transaction processed successfully' });
          this.loadTransactions();
          this.closeConfirmModal(); // Close modal only on success
        } else {
          // Display error message from API response
          const errorMessage = response.message || 'Failed to process transaction.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
        }
        this.isUpdating = false;
      },
      error: (error) => {
        console.error(`Error ${action}ing transaction:`, error);
        // Display error message from HTTP error response
        const errorMessage = error.error?.message || 'An unexpected error occurred.';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
        this.isUpdating = false;
      }
    });
  }

  onPageChange(event: any): void {
    this.currentPage = Math.floor(event.first / event.rows) + 1;
    this.itemsPerPage = event.rows;
    this.loadTransactions();
  }

  formatCurrency(amount: any): string {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  refreshTransactions(): void {
    this.loadTransactions();
  }

  isFormValid(): boolean {
    return true;
  }
}
